import { IMyLibRecordingsProps, IRecordingsLang } from './types';
import React from 'react';
import { alpha, Button, CircularProgress, MenuItem, Select, Stack, SxProps, Theme, Tooltip } from '@mui/material';
import { useIsMobileLayout } from '../../../../../../common/hooks/useIsMobileLayout';
import { theme } from '../../../../../../common/styles/theme';
import { TranslationsIcon } from '../../../../../../../icons/TranslationsIcon';
import { Text } from '../../../../../../common/components/Text/Text';
import { TAudioType, TParticipant, TRecording } from '../../../../../store/models/types';
import LangFlag from '../../../../shared/LangFlag/LangFlag';
import { AvatarName, isAvatarNameTruncated } from '../../../../shared/AvatarName/AvatarName';
import { RecordingTile } from '../../../../shared/RecordingTile/RecordingTile';
import { ArrowIcon } from '../../../../../../../icons/ArrowIcon';

const VISIBLE_PARTICIPANTS = 6;

const mobileSelectSx: SxProps<Theme> = {
	flex: '1 1 0',
	minWidth: theme.spacing(17),
	background: theme.palette.common.white,

	'.MuiSelect-select': {
		display: 'flex',
		alignItems: 'center',
		minWidth: 0,
	},
};

const participantChipSx: SxProps<Theme> = {
	height: theme.spacing(3.5),
	p: theme.spacing(.25, 1),
	textTransform: 'capitalize',
	color: theme.palette.grey[600],
	background: theme.palette.grey[50],

	display: 'flex',
	maxWidth: theme.spacing(20),
	overflow: 'hidden',

	'&:hover': {
		color: theme.palette.grey[900],
		background: theme.palette.grey[100],
	},

	'&.active': {
		color: theme.palette.common.white,
		background: theme.palette.primary.main,
	},

	'&.participants-more': {
		p: theme.spacing(.25, 2),
		color: theme.palette.grey[500],
		background: theme.palette.grey[200],

		'&:hover': {
			color: theme.palette.grey[900],
			background: theme.palette.grey[300],
		},
	},
};

export function MyLibRecordings({
	groups,
	isLoading,
	me,
	nativeLang,
	showNativeInitial,
	onShowNativeChange,
	onClick,
	onDelete,
	onDeleteAudio,
	onDeleteVideo,
	onDeleteVideoAndAudio,
	onDeleteForEveryone,
	onDownload,
	onDownloadCaptions,
	onDownloadCaptionsWithTimecodes,
	onCreateBook,
	onOpenBook,
	onOpenExercises,
	onReprocess,
	onProcess,
	onCancel,
	onCloseSettings,
}: IMyLibRecordingsProps) {

	const [groupsTab, setGroupsTab] = React.useState<IRecordingsLang[]>(null as any);
	const [participants, setParticipants] = React.useState<TParticipant[]>(null as any);
	const [participant, setParticipant] = React.useState<number>(0);
	const [lang, setLang] = React.useState<string>('all');
	const [showNative, setShowNative] = React.useState<boolean>(!!showNativeInitial);
	const [showAllParticipants, setShowAllParticipants] = React.useState<boolean>(false);
	const [truncatedId, setTruncatedId] = React.useState<number>(0);
	const isMobileLayout = useIsMobileLayout();
	// nothing to switch to (e.g. native == lesson language everywhere) — no switcher, target shown
	const hasNativeInfo = React.useMemo(() => groups.some(g => g.audios?.some(a => a.titleNative || a.descriptionNative)), [groups]);
	const canShowNative = !!nativeLang && hasNativeInfo;

	const showLangs = groups.length > 1;
	// если выбранного языка больше нет в группах (например, удалили все записи),
	// откатываемся на 'all', чтобы не застрять на пустом экране без табов
	const showAllLangs = lang === 'all' || !groups.some(g => g.langCode === lang);

	const onShowNative = (val: boolean) => {
		setShowNative(val);
		onShowNativeChange(val);
	}

	const onLangChange = (val: string) => {
		setLang(val);
		setParticipant(0);
	}

	// tooltip with the full name only when the chip cuts it
	const onNameHover = (e: React.MouseEvent<HTMLElement>, id: number) => {
		setTruncatedId(isAvatarNameTruncated(e.currentTarget) ? id : 0);
	};

	// top-N in the row, "+N" draws the rest; a picked hidden one takes the last slot when collapsed
	const visibleParticipants = React.useMemo(() => {
		if (!participants) return [];
		if (showAllParticipants) return participants;
		const top = participants.slice(0, VISIBLE_PARTICIPANTS + 1); // + 'All'
		const picked = participants.slice(VISIBLE_PARTICIPANTS + 1).find(p => p.id === participant);
		return picked ? [...top.slice(0, -1), picked] : top;
	}, [participants, participant, showAllParticipants]);
	const hiddenCount = (participants?.length || 0) - visibleParticipants.length;

	const renderVideo = (group: IRecordingsLang, item: TRecording, withFlag: boolean) => (
		<RecordingTile
			key={group.id + '-' + item.id + (item.type === TAudioType.CONFERENCE)}
			item={item}
			me={me}
			isBig
			withFlag={withFlag}
			showNative={canShowNative && showNative}
			onClick={(id) => onClick(group.id, id)}
			onDelete={(id) => onDelete(group.id, id)}
			onDeleteAudio={(id) => onDeleteAudio(group.id, id)}
			onDeleteVideo={(id) => onDeleteVideo(group.id, id)}
			onDeleteVideoAndAudio={(id) => onDeleteVideoAndAudio(group.id, id)}
			onDeleteForEveryone={onDeleteForEveryone}
			onDownload={onDownload}
			onDownloadCaptions={onDownloadCaptions}
			onDownloadCaptionsWithTimecodes={onDownloadCaptionsWithTimecodes}
			onCreateBook={onCreateBook}
			onOpenBook={onOpenBook}
			onOpenExercises={onOpenExercises}
			onReprocess={onReprocess}
			onProcess={onProcess}
			onCancel={onCancel}
			onCloseSettings={onCloseSettings}
		/>
	);

	React.useEffect(() => {

		let groupsTab = null as any;
		if (groups.length) {
			const filtered = showAllLangs ? groups : groups.filter(g => g.langCode === lang);
			groupsTab = filtered.length ? filtered : null;
		}
		setGroupsTab(groupsTab);

		// most calls first, most recently met among equals
		const met = new Map<number, { participant: TParticipant, calls: number, time: number }>();
		if (groupsTab) {
			groupsTab.forEach((group: IRecordingsLang) => {
				group?.audios?.forEach((item: TRecording) => {
					const time = new Date(item.createdAt).getTime() || 0;
					item.participants?.forEach(p => {
						const prev = met.get(p.id);
						met.set(p.id, prev
							? { participant: prev.time < time ? p : prev.participant, calls: prev.calls + 1, time: Math.max(prev.time, time) }
							: { participant: p, calls: 1, time }
						);
					});
				});
			});
		}
		setParticipants([
			{ id: 0, name: 'All' },
			...Array.from(met.values())
				.sort((a, b) => b.calls - a.calls || b.time - a.time)
				.map(({ participant }) => participant),
		]);

	}, [groups, lang]);

	const showParticipants = !!participants?.length && participants.length > 2;

	// titles/descriptions in the lesson language or translated into the user's native one
	const translateButton = canShowNative && !!nativeLang &&
		<Tooltip
			title={`Show titles and descriptions in ${nativeLang.name}`}
		>
			<Button
				className={showNative ? 'active' : ''}
				variant={'outlined'}
				onClick={() => onShowNative(!showNative)}
				sx={{
					height: theme.spacing(isMobileLayout ? 5 : 3.5),
					p: theme.spacing(.25, 1),
					whiteSpace: 'nowrap',
					color: theme.palette.grey[500],
					borderColor: theme.palette.grey[300],
					background: theme.palette.grey[50],

					'&.active': {
						color: theme.palette.primary.main,
						borderColor: theme.palette.primary.main,
						background: alpha(theme.palette.primary.main, .1),
					},

					'&:hover': {
						background: 'none',
					},

					'.MuiButton-startIcon': {
						color: 'inherit',
					},
				}}
				startIcon={<TranslationsIcon sx={{ fontSize: theme.spacing(2.25) }} />}
			>
				Translate titles
			</Button>
		</Tooltip>;

	if (isLoading) {
		return <Stack
			sx={{
				width: 1,
				height: 1,
				p: 4,
			}}
			alignItems={'center'}
			justifyContent={'center'}
		>
			<CircularProgress />
		</Stack>
	}

	return (<Stack
		gap={2}
		sx={{
			height: 1,
			minHeight: '50vh',
			py: theme.spacing(2),
		}}
	>
		{!groups.length
			? <Stack
				sx={{
					justifyContent: 'center',
					alignItems: 'center',
					p: theme.spacing(2),
					height: 1,
					minHeight: '50vh',
				}}
			>
				<Text
					sx={{
						fontSize: theme.typography.h3,
						color: theme.palette.grey[500],
					}}
				>
					{'You have no recordings yet'}
				</Text>
			</Stack>
			: <>
				{!isMobileLayout && (showLangs || canShowNative) &&
					<Stack
						direction={'row'}
						alignItems={'center'}
						gap={3}
						rowGap={1}
						flexWrap={'wrap'}
					>
						{showLangs && <Stack
							direction={'row'}
							alignItems={'center'}
							gap={1}
							rowGap={1}
							flexWrap={'wrap'}
						>
							<Button
								className={`${showAllLangs ? 'active' : ''}`}
								variant={'contained'}
								sx={{
									height: theme.spacing(3.5),
									p: theme.spacing(.25, 1),
									color: theme.palette.grey[600],
									background: theme.palette.grey[50],

									'&:hover': {
										color: theme.palette.grey[900],
										background: theme.palette.grey[100],
									},

									'&.active': {
										color: theme.palette.common.white,
										background: theme.palette.primary.main,
									}
								}}
								onClick={() => onLangChange('all')}
								key={'lang-all'}
							>
								All languages
							</Button>
							{groups.map(g => {
								return (
									<Button
										className={`${lang === g.langCode ? 'active' : ''}`}
										variant={'contained'}
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											height: theme.spacing(3.5),
											p: theme.spacing(.25, 1),
											color: theme.palette.grey[600],
											background: theme.palette.grey[50],

											'&:hover': {
												color: theme.palette.grey[900],
												background: theme.palette.grey[100],
											},

											'&.active': {
												color: theme.palette.common.white,
												background: theme.palette.primary.main,
											}
										}}
										onClick={() => onLangChange(g.langCode)}
										key={`lang-${g.id}`}
									>
										<LangFlag
											code={g.langCode}
										/>
										{g.langName}
									</Button>
								);
							})}
						</Stack>}

						{translateButton}
					</Stack>
				}

				{isMobileLayout && (showLangs || showParticipants || !!translateButton) &&
					<Stack
						direction={'row'}
						alignItems={'center'}
						gap={1}
						flexWrap={'wrap'}
					>
						{showLangs &&
							<Select
								size={'small'}
								value={showAllLangs ? 'all' : lang}
								onChange={(e) => onLangChange(e.target.value)}
								sx={mobileSelectSx}
							>
								<MenuItem value={'all'}>All languages</MenuItem>
								{groups.map(g => (
									<MenuItem value={g.langCode} key={`lang-${g.id}`}>
										<Stack direction={'row'} alignItems={'center'} gap={1}>
											<LangFlag code={g.langCode} />
											{g.langName}
										</Stack>
									</MenuItem>
								))}
							</Select>
						}
						{showParticipants &&
							<Select
								size={'small'}
								value={participant}
								onChange={(e) => setParticipant(+e.target.value)}
								sx={mobileSelectSx}
							>
								{participants.map(p => (
									<MenuItem value={p.id} key={`p-${p.id}`}>
										{p.id
											? <AvatarName name={p.name} picture={p.picture} size={2} />
											: 'All participants'
										}
									</MenuItem>
								))}
							</Select>
						}
						{translateButton}
					</Stack>
				}

				{!isMobileLayout && showParticipants &&
					<Stack
						direction={'row'}
						gap={1}
						rowGap={1}
						flexWrap={'wrap'}
					>
						{visibleParticipants.map(p => {
							return (
								<Tooltip
									title={p.id && truncatedId === p.id ? p.name : ''}
									placement={'top'}
									enterDelay={500}
									disableInteractive
									key={`p-${p.id}`}
								>
									<Button
										className={`${participant === p.id ? 'active' : ''}`}
										variant={'contained'}
										sx={participantChipSx}
										onClick={() => setParticipant(p.id)}
										onMouseEnter={(e) => onNameHover(e, p.id)}
									>
										{p.id
											? <AvatarName
												name={p.name}
												picture={p.picture}
												size={2}
											/>
											: p.name
										}
									</Button>
								</Tooltip>
							);
						})}
						{hiddenCount > 0 && <>
							...
							<Button
								className={`participants-more`}
								variant={'contained'}
								sx={participantChipSx}
								onClick={() => setShowAllParticipants(true)}
							>
								{`+${hiddenCount}`}
							</Button>
						</>}
						{showAllParticipants && participants.length > VISIBLE_PARTICIPANTS + 1 &&
							<Button
								className={`participants-more`}
								variant={'contained'}
								sx={participantChipSx}
								startIcon={<ArrowIcon sx={{
									width: theme.spacing(1.5),
									height: theme.spacing(1.5),
									mr: 0,
									transform: 'rotate(180deg)',
								}} />}
								onClick={() => setShowAllParticipants(false)}
							>
								Less
							</Button>
						}
					</Stack>
				}

				{!!groupsTab?.length && (showAllLangs
					? <Stack>
						{groupsTab
							.flatMap(group => (group.audios || []).map(item => ({ group, item })))
							.filter(({ item }) => !participant || item.participants.some(p => p.id === participant))
							.sort((a, b) => new Date(b.item.createdAt).getTime() - new Date(a.item.createdAt).getTime())
							.map(({ group, item }) => renderVideo(group, item, true))
						}
					</Stack>
					: groupsTab.map(group => (
						<Stack key={`groups-lang-${group.id}`}>
							{group?.audios?.map(item => {
								if (participant && !item.participants.some(p => p.id === participant)) return null;
								return renderVideo(group, item, false);
							})}
						</Stack>
					))
				)}

			</>
		}
	</Stack>);
}
